import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Divider,
} from "@fluentui/react-components";
import { ArrowLeftRegular } from "@fluentui/react-icons";
import { Constants } from "common/constants";
import { useNavigate } from "react-router-dom";
import { useHelpStyles } from "./help.styles";

export const Help = (): JSX.Element => {
  const navigate = useNavigate();
  const styles = useHelpStyles();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Button
          appearance="outline"
          icon={<ArrowLeftRegular />}
          onClick={() => {
            navigate("/");
          }}
          className={styles.backButton}
        >
          Back to Home
        </Button>
        <h1 className={styles.title}>AIMS Help</h1>
      </div>
      <Divider />
      <div className={styles.welcomeCard}>
        Welcome to AIMS Help. Here you'll find answers to frequently asked
        questions about using the AI Made Simple platform. Click on any question
        to expand the answer.
      </div>
      <Accordion className={styles.accordionContainer} collapsible multiple>
        <AccordionItem value="1" className={styles.accordionItem}>
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            What is AIMS?
          </AccordionHeader>
          <AccordionPanel>
            <p>
              Developed in-house, is a Large Language Model (LLM) wrapper with
              an intuitive, North Highland-branded interface. The web tool
              seamlessly integrates Claude, ChatGPT, Llama, and NH-enhanced
              models based on those three, allowing us to remain adaptable and
              agnostic in the ever-evolving world of AI. By leveraging multiple
              LLMs, we ensure our clients always have access to cutting-edge AI
              capabilities tailored to their specific needs.
            </p>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem value="2" className={styles.accordionItem}>
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            What are LLMs?
          </AccordionHeader>
          <AccordionPanel>
            <p>
              An LLM, or Large Language Model, is a type of artificial
              intelligence that specializes in understanding and generating
              human-like text. It's essentially a giant computer program that
              has been trained on an enormous amount of written data, like
              books, articles, and websites.
            </p>
            <p>
              By analyzing all this data, the LLM learns patterns and
              relationships between words, allowing it to understand the meaning
              and context of language. This enables it to perform various
              language-related tasks, such as answering questions, generating
              text, translating languages, and even engaging in conversations.
            </p>
            <p>
              What makes LLMs impressive is their ability to handle a wide range
              of topics and adapt to different writing styles. They can help
              with tasks like writing articles, composing emails, or even
              creating stories and poems. As they continue to improve, LLMs are
              becoming increasingly useful tools for businesses, researchers,
              and individuals looking to streamline their work or explore new
              creative possibilities with language.
            </p>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem className={styles.accordionItem} value="3">
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            How do I switch between models?
          </AccordionHeader>
          <AccordionPanel>
            <p>
              Right above the space where you type in the prompt you will find a
              dropdown button where you can select between:
            </p>
            <ul>
              <li>
                <span>Claude (Sonnet 3.7)</span>
              </li>
              <li>
                <span>Claude (Sonnet 3.5)</span>
              </li>
              <li>
                <span>GPT (gpt-4o)</span>
              </li>
              <li>
                <span>Llama (3.1 - 8B Instruct ) </span>
              </li>
              <li>
                Or a version of any of the above but with added North Highland
                knowledge to pull from (&ldquo;+nhqa&rdquo;)
              </li>
            </ul>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem className={styles.accordionItem} value="4">
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            What is Generative AI and how does it work?
          </AccordionHeader>
          <AccordionPanel>
            <p>
              Ethan Mollick is a well respected expert in this industry, so we
              won&rsquo;t reinvent the wheel and we&rsquo;ll just point you to
              his great article:&nbsp;
              <a href="https://www.oneusefulthing.org/p/thinking-like-an-ai">
                Thinking Like an AI
              </a>
            </p>
            <p>
              If you want to lean more, we highly recommend his book&nbsp;
              <a href="https://www.google.com/search?gs_ssp=eJzj4tVP1zc0LEuyLKusqog3YPQSTs7PzCtJzcnJTE_NS05VSMrPzwYA3vYMrg&q=cointelligence+book&rlz=1C1GCEA_enUS1101US1101&gs_lcrp=EgZjaHJvbWUqDAgBEC4YJxiABBiKBTIPCAAQIxgnGOMCGIAEGIoFMgwIARAuGCcYgAQYigUyBggCEEUYQDIJCAMQRRg5GIAEMgcIBBAAGIAEMgcIBRAAGIAEMgcIBhAAGIAEMgcIBxAAGIAE0gEIMjA2MmowajSoAgCwAgE&sourceid=chrome&ie=UTF-8&si=ACC90nyLlcElEvb2bNLvGYvz3qiAqB2xBk-RCAO1bErVf4zAGKVMmEUbJA5gVss7-pt_Ukedf7BvErXbfJOmWxIbd6c1QB3rZZ94nGoE2bixrm16ermo4YFxH5Bi9VbSckiVpC_wmi7AcJzGh7QXD__jOTt8YcHjig%3D%3D&ictx=1&ved=2ahUKEwjSvaW__6SJAxXhTTABHa27CLAQ_coHegQILhAB&safe=active&ssui=on">
                Co-Intelligence: Living and Working with AI
              </a>
              &nbsp;as an easy, fun, but highly educational read. &nbsp;as an
              easy, fun, but highly educational read.
            </p>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem className={styles.accordionItem} value="5">
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            Can the tool remember my previous conversations?
          </AccordionHeader>
          <AccordionPanel>
            <p>
              No, AIMS doesn't retain conversation history across chats,
              ensuring your privacy with each session. Your queries are handled
              independently each time.
            </p>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem className={styles.accordionItem} value="6">
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            How do I use this tool effectively for brainstorming, content
            creation, or research?
          </AccordionHeader>
          <AccordionPanel>
            <p>
              Be clear and specific with your prompts. Mention any specific
              information you&rsquo;re seeking or the tone you want. Since each
              model may interpret questions slightly differently, experimenting
              with prompt wording can help you get a broader range of responses.
            </p>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem className={styles.accordionItem} value="7">
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            Who can see my chats?
          </AccordionHeader>
          <AccordionPanel>
            <p>
              Only you can see your chats, unless you intentionally share a chat
              (with specific users, groups, or everyone).
            </p>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem className={styles.accordionItem} value="8">
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            What types of tasks can this tool help me with?
          </AccordionHeader>
          <AccordionPanel>
            <p>
              You can use it for brainstorming, research, writing assistance,
              learning new concepts, answering questions, language translation,
              coding help, and more. Different LLMs may excel in various areas,
              so you&rsquo;ll benefit from a variety of expertise.
            </p>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem className={styles.accordionItem} value="9">
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            Are there tasks this tool cannot do?
          </AccordionHeader>
          <AccordionPanel>
            <p>
              While the models are versatile, they don&rsquo;t replace
              specialized human expertise. The more important the ask, the more
              you should double-check the response with a human pair of eyes.
            </p>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem className={styles.accordionItem} value="10">
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            How accurate and reliable are the responses?
          </AccordionHeader>
          <AccordionPanel>
            <p>
              Each LLM&rsquo;s response varies based on its training and
              capabilities, and while they are accurate within many domains,
              their answers should be double-checked, especially for factual or
              sensitive topics.
            </p>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem className={styles.accordionItem} value="11">
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            How should I phrase my questions for the best responses?
          </AccordionHeader>
          <AccordionPanel>
            <p>
              Be as clear and specific as possible. Use keywords or phrases that
              relate directly to what you need. If you&rsquo;re not satisfied
              with the first response, try rephrasing or asking the tool to
              refine its answer.
            </p>
          </AccordionPanel>
        </AccordionItem>
        <AccordionItem className={styles.accordionItem} value="12">
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            Can I ask the tool to revise or expand on answers?
          </AccordionHeader>
          <AccordionPanel>
            <p>
              Absolutely. If a response isn&rsquo;t quite what you were looking
              for, follow up with additional details or ask for a different
              perspective. The models can adapt their responses to suit your
              needs.
            </p>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem className={styles.accordionItem} value="13">
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            Can I request specific styles or tones in the responses?
          </AccordionHeader>
          <AccordionPanel>
            <p>
              Yes, you can. For instance, you could ask for a formal tone, a
              humorous style, or a concise summary. Each LLM will adjust
              accordingly, often giving you a range of styles to choose from.
            </p>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem className={styles.accordionItem} value="14">
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            What are Knowledge Components?
          </AccordionHeader>
          <AccordionPanel>
            <p>
              Knowledge Components are like digital snapshots of expertise,
              data, or processes that you can share with AI to make
              conversations more relevant and precise. Think of them as focused
              packets of information that give AI tools context about your
              specific needs. These could be:
            </p>
            <ul>
              <li>A document capturing your company&apos;s writing style</li>
              <li>Historical project performance data</li>
              <li>Client feedback patterns</li>
              <li>Product specifications</li>
              <li>Market research findings</li>
              <li>Successful proposals or presentations</li>
            </ul>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem className={styles.accordionItem} value="15">
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            What file types are supported in the chat upload functionality?
          </AccordionHeader>
          <AccordionPanel>
            <p>The following file types are supported:</p>
            <ul>
              <li>
                <b>Documents:</b> PDF, DOCX, TXT, MD
              </li>
              <li>
                <b>Images:</b> PNG, JPG, JPEG, GIF, WEBP
              </li>
            </ul>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem className={styles.accordionItem} value="16">
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            What should I do if I&rsquo;m not satisfied with a response?
          </AccordionHeader>
          <AccordionPanel>
            <p>
              Try rephrasing your question or providing additional context. Each
              model might interpret prompts slightly differently, so rephrasing
              can improve accuracy and relevance.
            </p>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem className={styles.accordionItem} value="17">
          <AccordionHeader
            className={styles.accordionHeader}
            expandIconPosition="end"
          >
            Why do I sometimes get different answers to the same question?
          </AccordionHeader>
          <AccordionPanel>
            <p>
              Each LLM has unique knowledge and training, so variations in
              responses are natural. If you want a consistent answer, try
              rephrasing or specifying which type of information you need. And
              remember, LLMs are a predictive technology.
            </p>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
      <div className={styles.contactCard}>
        <div>I didn&rsquo;t find an answer here, what do I do next?</div>
        <div>
          Come talk to us. Email us at{" "}
          <a href={Constants.ISSUE_URL_MAILTO} className={styles.contactLink}>
            {Constants.ISSUE_URL}
          </a>
        </div>
      </div>
    </div>
  );
};
